import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ColDef, ColumnState, IServerSideDatasource, Column } from '@ag-grid-community/core';
import { TableColumnFormat, FormatStyle, ExportData, UniqueValuesData } from './types';
import { ViewColumn } from 'common/types/viewColumn';
import { ColumnAggregation } from 'common/visualizations/dataProviders/types';
import { Vif, Hierarchy, OrderConfig, LinkedAction, CustomSortConfig } from 'common/visualizations/vif';
import { get } from 'lodash';
import HierarchyTabs from './HierarchyTabs';
import { Grid } from './Grid';
import { AgGridProps } from './types/AgGridProps';
import classNames from 'classnames';
import { Filters } from 'common/components/FilterBar/types';
import getDefaultDomain from 'common/visualizations/helpers/getDefaultDomain';
import { getParameterOverrides, getRowStripeStyle } from 'common/visualizations/helpers/VifSelectors';
import { DEFAULT_TABLE_HEADER_TEXT_COLOR, DEFAULT_TABLE_HEADER_BACKGROUND_COLOR } from './Constants';
import { ClientContextVariable } from 'common/types/clientContextVariable';
import eventBus from './helpers/EventBus';
import { AgColumnFilter } from 'common/types/agGrid/filters';
import { ContextParams } from './helpers/processDiscoveryHelper';
import SoqlHelpers from 'common/visualizations/helpers/SoqlHelpers';
import { GrandTotalRow } from 'common/types/agGrid/grandTotal';

export interface AgGridContainerProps {
  vif: Vif;
  columnMetadata: ViewColumn[];
  nonStandardAggregations: ColumnAggregation[] | null;
  datasource: IServerSideDatasource;
  datasetName?: string;
  getGrandTotalRow: (hierarchyId: string) => Promise<GrandTotalRow>;
  onColumnPivotChange: (columnState: ColumnState[], columns: Column[], hierarchyId?: string) => void;
  onColumnPivotModeChange: (pivotMode: boolean, hierarchyId?: string) => void;
  onColumnReorder: (columnState: ColumnState[]) => void;
  onColumnResize: (columns: Column[] | null) => void;
  onColumnRowGroupChange: (columnState: ColumnState[], columns: Column[], hierarchyId?: string) => void;
  onColumnSort: (columnState: ColumnState[], hierarchyId?: string) => void;
  onColumnValueChange: (columns: Column[], hierarchyId?: string) => void;
  onColumnVisibilityChange: (
    columnState: ColumnState[],
    columns: Column[] | null,
    hierarchyId?: string,
  ) => void;
  onHierarchyTabChange: (hierarchyId: string) => void;
  onFilterChange: (newFilters: Filters, agFilterModel?: AgColumnFilter) => void;
  paginationPageSize?: number;
  defaultColDefOverrides?: ColDef;
  displayColumnFilters?: boolean;
  useSetFilters?: boolean;
  agFilterModel?: AgColumnFilter;
  showAgGridColumnMenu?: boolean;
  showAgGridColumnAggregations?: boolean;
  isIndented?: boolean;
  openToolPanelByDefault?: boolean;
  vizUid?: string;
  getExportData?: (selectedFiltered: boolean) => Promise<ExportData>;
  actionColumnDef?: ColDef;
  processDiscoveryContext: ContextParams;
  newTableActions: string[];
  showColumnMenuKebab?: boolean;
  getUniqueValues?: (
    columns: ViewColumn[],
    order: OrderConfig[],
    customSortConfig: CustomSortConfig | undefined,
  ) => Promise<UniqueValuesData>;
  handleCustomSortCycle: (newOrderConfig: OrderConfig[], hierarchyId?: string) => void;
  handleIndentedHeaderSortIndex?: (headerSortCycleIndex: number, hierarchyId?: string) => void;
}

const GridContainer = (props: AgGridContainerProps) => {
  const {
    vif,
    columnMetadata,
    nonStandardAggregations,
    datasource,
    datasetName,
    getGrandTotalRow,
    onColumnPivotChange,
    onColumnPivotModeChange,
    onColumnReorder,
    onColumnResize,
    onColumnRowGroupChange,
    onColumnSort,
    onColumnValueChange,
    onColumnVisibilityChange,
    onHierarchyTabChange,
    onFilterChange,
    paginationPageSize,
    defaultColDefOverrides,
    displayColumnFilters,
    useSetFilters,
    agFilterModel,
    showAgGridColumnMenu,
    showAgGridColumnAggregations,
    openToolPanelByDefault,
    vizUid,
    getExportData,
    actionColumnDef,
    processDiscoveryContext,
    newTableActions,
    showColumnMenuKebab,
    getUniqueValues,
    handleCustomSortCycle,
    handleIndentedHeaderSortIndex,
  } = props;

  const [isPrintModalOpen, setIsPrintModalOpen] = useState(false);
  const openPrintModal = useCallback(() => setIsPrintModalOpen(true), []);
  const closePrintModal = useCallback(() => setIsPrintModalOpen(false), []);

  useEffect(() => {
    eventBus.on(`print-table-${vizUid}`, openPrintModal);

    return () => {
      eventBus.remove(`print-table-${vizUid}`, openPrintModal);
    };
  }, [openPrintModal, vizUid]);

  const vifHierarchies: Hierarchy[] = get(vif, 'series[0].dataSource.hierarchies', []);
  const vifFilters: Filters = get(vif, 'series[0].dataSource.filters', []);
  const headerFormat: FormatStyle = get(vif, 'series[0].formatting.headerFormat', {});
  const vifParameterOverrides: ClientContextVariable[] = getParameterOverrides(vif);
  const showExistingActionColumn = get(vif, 'configuration.showExistingActionColumn', false);
  const selectedExistingColumn = get(vif, 'configuration.selectedExistingColumn', '');
  const selectedActionForExistingColumn = get(vif, 'configuration.selectedActionForExistingColumn', '');
  const linkedAction: LinkedAction = showExistingActionColumn
    ? { selectedExistingColumn, selectedActionForExistingColumn }
    : { selectedExistingColumn: '', selectedActionForExistingColumn: '' };

  // Creates a map where the column 'fieldName' is the key and the value is a boolean field from the columns metadata which has the 'hidden' flag.
  const { columnFieldnameIsHiddenMap } = columnMetadata.reduce(
    (acc, columnMeta) => {
      acc.columnFieldnameIsHiddenMap[columnMeta.fieldName] = columnMeta.flags
        ? columnMeta.flags.includes('hidden')
        : false;
      return acc;
    },
    {
      columnFieldnameIsHiddenMap: {} as Record<string, boolean>,
    },
  );

  const vifColumns: ViewColumn[] = useMemo(
    () =>
      get(vif, 'series[0].dataSource.dimension.columns', []).filter(
        (column: ViewColumn) => !columnFieldnameIsHiddenMap[column.fieldName],
      ),
    [vif, columnFieldnameIsHiddenMap],
  );
  const activeHierarchyId: string | undefined = get(vif, 'series[0].dataSource.activeHierarchyId');

  const isHierarchyActive = (hierarchy: Hierarchy) => {
    return hierarchy.id === activeHierarchyId;
  };

  const columnFormats: { [key: string]: TableColumnFormat } = get(
    props.vif,
    'series[0].formatting.columnFormat',
    {},
  );

  const filterWhereClause = SoqlHelpers.whereClauseFilteringOwnColumn(vif, 0);

  const commonGridProps = {
    actionColumnDef,
    activeHierarchyId,
    agFilterModel,
    columnFormats,
    columnMetadata,
    datasetName,
    datasetUid: get(vif, 'series[0].dataSource.datasetUid'),
    datasource,
    defaultColDefOverrides,
    description: get(props.vif, 'description'),
    displayColumnFilters,
    domain: get(vif, 'series[0].dataSource.domain') || getDefaultDomain(),
    filterWhereClause,
    getExportData,
    getGrandTotalRow,
    getUniqueValues,
    handleCustomSortCycle,
    handleIndentedHeaderSortIndex,
    headerFormat,
    isIndented: get(vif, 'configuration.isIndented'),
    linkedAction,
    newTableActions,
    nonStandardAggregations,
    onColumnPivotChange,
    onColumnPivotModeChange,
    onColumnReorder,
    onColumnResize,
    onColumnRowGroupChange,
    onColumnSort,
    onColumnValueChange,
    onColumnVisibilityChange,
    onFilterChange,
    openToolPanelByDefault,
    paginationPageSize,
    processDiscoveryContext,
    rowStripeStyle: getRowStripeStyle(vif),
    searchString: get(vif, 'series[0].dataSource.searchString'),
    showAgGridColumnAggregations,
    showAgGridColumnMenu,
    showColumnMenuKebab,
    title: get(props.vif, 'title'),
    useSetFilters,
    vifColumns,
    vifFilters,
    vifParameterOverrides,
    vizUid,
  } satisfies Partial<AgGridProps>; // No unknown props, undefined is fine tho.

  const isBoldHeaderFontStyle =
    headerFormat?.fontStyle?.isBold === undefined ? true : headerFormat.fontStyle.isBold;
  const isItalicHeaderFontStyle =
    headerFormat?.fontStyle?.isItalic === undefined ? false : headerFormat.fontStyle.isItalic;
  const style = {
    '--ag-header-foreground-color': headerFormat?.textColor || DEFAULT_TABLE_HEADER_TEXT_COLOR,
    '--ag-header-background-color': headerFormat?.backgroundColor || DEFAULT_TABLE_HEADER_BACKGROUND_COLOR,
    '--ag-table-header-font-weight': isBoldHeaderFontStyle ? 'bold' : 'normal',
    '--ag-table-header-font-style': isItalicHeaderFontStyle ? 'italic' : 'normal',
  };
  const gridClasses = classNames('ag-grid-react ag-theme-material', {
    'ag-grid-columns-aggregation': showAgGridColumnAggregations ?? true,
  });
  return (
    <div className="ag-grid-visualization">
      <HierarchyTabs
        activeHierarchyId={activeHierarchyId}
        onTabChange={(newTabId) => onHierarchyTabChange(newTabId)}
        hierarchies={vifHierarchies}
        columns={props.columnMetadata}
      />
      {vifHierarchies.map((hierarchy) => {
        const vifOrderConfig: OrderConfig[] = hierarchy?.order ?? [];
        const agGridOpenNodeLevel = hierarchy?.agGridOpenNodeLevel ?? 0;

        const classes = classNames(gridClasses, { 'ag-hidden': !isHierarchyActive(hierarchy) });

        return (
          <div id={`aggrid-${hierarchy.id}`} style={style} className={classes} key={hierarchy.id}>
            <Grid
              {...{ ...commonGridProps, vifOrderConfig, agGridOpenNodeLevel }}
              hierarchyConfig={hierarchy}
              isPrintModalOpen={isPrintModalOpen && activeHierarchyId === hierarchy.id}
              onPrintModalClose={closePrintModal}
            />
          </div>
        );
      })}
    </div>
  );
};

export default GridContainer;
